import { X, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { usePlayer } from "@/visitor/PlayerContext";
import { fmtTime } from "@/lib/api";

export default function LanguageSheet() {
  const { sheetOpen, closeLanguageSheet, languages, lang, setLang, t, audioAvailability } = useI18n();
  const { track, switchLanguage, resumeTimeFor, hasAudio } = usePlayer();
  if (!sheetOpen) return null;
  const artwork = track?.artwork;
  const isAvailable = (code) => {
    if (artwork) return !!artwork.content?.[code]?.audio_url;
    if (audioAvailability) return !!audioAvailability[code];
    return true;
  };

  const choose = (code) => {
    setLang(code);
    if (track) switchLanguage(code);
    closeLanguageSheet();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center" data-testid="language-sheet">
      <div role="presentation" onClick={closeLanguageSheet} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-testid="language-sheet-backdrop" />
      <div className="relative w-full sm:max-w-md bg-[#161616] border-t sm:border border-[var(--line)] p-6 pb-10 sm:pb-6 fade-up max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl uppercase" data-testid="language-sheet-title">{t("language_sheet_title")}</h2>
          <button type="button" onClick={closeLanguageSheet} aria-label={t("close")} className="ctrl -mr-3" data-testid="language-sheet-close"><X size={22} /></button>
        </div>
        {artwork && (
          <p className="text-sm text-[var(--muted)] mt-3" data-testid="language-sheet-note">
            {t("stay_note", { artwork: artwork.title })}{hasAudio ? ` ${t("resume_note", { time: fmtTime(resumeTimeFor()) })}` : ""}
          </p>
        )}
        <ul className="mt-5 divide-y divide-[var(--line)]" role="listbox">
          {languages.map((l) => {
            const avail = isAvailable(l.code);
            const current = l.code === lang;
            return (
              <li key={l.code}>
                <button type="button" role="option" aria-selected={current} onClick={() => choose(l.code)} data-testid={`language-option-${l.code}`}
                  className="w-full min-h-[56px] flex items-center gap-4 py-3 px-2 text-left hover:bg-[var(--surface)] active:bg-[var(--surface-2)] transition-colors">
                  <span className="w-9 text-xs font-bold tracking-widest uppercase text-[var(--muted)] pointer-events-none">{l.code}</span>
                  <span className="flex-1 pointer-events-none">
                    <span className="block">{l.native_name || l.name}</span>
                    {l.native_name && l.native_name !== l.name && <span className="block text-xs text-[var(--muted)]">{l.name}</span>}
                  </span>
                  {!avail && <span className="text-[0.6rem] tracking-[0.2em] uppercase border border-[var(--line)] px-2 py-0.5 text-[var(--muted)] pointer-events-none" data-testid={`language-text-only-${l.code}`}>{t("text_only")}</span>}
                  {current && <Check size={18} className="text-accent pointer-events-none" data-testid="language-current-check" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
